import { LitElement, PropertyValues, html } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { LSystemFactory } from "./lsystem-factory";

@customElement("pos-photosynth")
export class PhotosynthElement extends LitElement {
    @property() value = "";

    @state() generation = 0;
    @state() maxGeneration = 0;
    @state() seed = "yd";
    @state() scale = 1.6;
    @state() positionX = 0.2;
    @state() isAutomaticGrowing = false;
    @query("canvas") canvas!: HTMLCanvasElement;
    @query("#container") canvasContainer!: HTMLDivElement;

    private animationFrameId: number | null = null;
    private startTime: number = 0;

    createRenderRoot() {
        return this;
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        const canvasContainerObserver = new ResizeObserver((entries) => {
            const container = entries[0];
            const { width, height } = container.contentRect;
            this.canvas.width = width;
            this.canvas.height = height;
            this.build();
        });

        canvasContainerObserver.observe(this.canvasContainer);
        this.startAnimation();
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
        }
    }

    private handleIteration(e: InputEvent) {
        const input = e.currentTarget as HTMLInputElement;
        const gen = parseInt(input.value);
        this.generation = gen;
    }

    private handleScale(e: InputEvent) {
        const input = e.currentTarget as HTMLInputElement;
        const scale = parseFloat(input.value);
        this.scale = scale;
    }

    private handlePositonX(e: InputEvent) {
        const input = e.currentTarget as HTMLInputElement;
        const pos = parseFloat(input.value);
        this.positionX = pos;
    }

    private handleSeed(e: InputEvent) {
        const input = e.currentTarget as HTMLInputElement;
        this.seed = input.value;
    }

    private handleGrowFully() {
        if (this.isAutomaticGrowing) {
            return;
        }
        this.generation = 0;
        const growCb = () => {
            setTimeout(() => {
                if (this.generation < this.maxGeneration - 1) {
                    this.isAutomaticGrowing = true;
                    this.generation += 1;
                    growCb();
                } else {
                    this.isAutomaticGrowing = false;
                }
            }, 50);
        };

        growCb();
    }

    private startAnimation() {
        this.startTime = performance.now();
        const animate = (currentTime: number) => {
            this.build(currentTime);
            this.animationFrameId = requestAnimationFrame(animate);
        };
        this.animationFrameId = requestAnimationFrame(animate);
    }

    private build(currentTime: number = performance.now()) {
        const ctx = this.canvas.getContext("2d");
        if (!ctx) {
            return;
        }

        const lsys = LSystemFactory.createSmallFern(this.seed);
        this.maxGeneration = lsys.maxGenerations;
        const dark = localStorage.getItem("bd-theme") === "dark";

        // sway angle with sine
        const elapsed = (currentTime - this.startTime) / 1000; // elapsed in seconds
        const swaySpeed = 0.1; // oscillations per second
        const swayAmplitude = 0.3; // max sway angle
        const swayAngle =
            Math.sin(elapsed * swaySpeed * Math.PI * 2) * swayAmplitude;

        const lsysBuilder = LSystemFactory.createDefaultModelBuilder(
            ctx,
            this.canvasContainer.clientWidth,
            this.canvasContainer.clientHeight,
            this.positionX,
            this.scale,
            dark,
            swayAngle,
        );
        lsysBuilder.build(lsys, this.generation);
    }

    render() {
        return html`
            <div id="container" class="canvas-container">
                <canvas></canvas>
                <input
                    type="range"
                    class="canvas-slider canvas-slider-pos"
                    value=${this.positionX}
                    min="0.03"
                    step="0.01"
                    max="0.97"
                    @input=${this.handlePositonX}
                    .disabled=${this.isAutomaticGrowing}
                />
                <input
                    type="range"
                    class="canvas-slider canvas-slider-scale"
                    value=${this.scale}
                    min="0.5"
                    step="0.1"
                    max="4"
                    @input=${this.handleScale}
                    .disabled=${this.isAutomaticGrowing}
                />
            </div>
            <fieldset class="bd-fieldset controls-container">
                <label>
                    <b>seed</b>
                    <input
                        type="text"
                        class="bd-input"
                        @input=${this.handleSeed}
                        value=${this.seed}
                        .disabled=${this.isAutomaticGrowing}
                    />
                </label>
                <label>
                    <b>age: ${this.generation}</b>
                    <input
                        class="bd-input"
                        value=${this.generation}
                        type="range"
                        min="0"
                        max="${this.maxGeneration - 1}"
                        @input=${this.handleIteration}
                        .disabled=${this.isAutomaticGrowing}
                    />
                </label>
                <div class="bd-flex bd-gap-xs">
                    <button
                        class="bd-button bd-width-full"
                        style="flex-shrink: 1;"
                        @click=${this.handleGrowFully}
                        .disabled=${this.isAutomaticGrowing}
                    >
                        grow
                    </button>
                    <bd-dialog
                        label="about photosynth"
                        class="description-mobile"
                    >
                        <template slot="trigger">
                            <button
                                class="bd-button bd-button-icon bd-surface-10"
                            >
                                <bd-icon value="question-mark"></bd-icon>
                            </button>
                        </template>
                        <template slot="content">
                            <div class="bd-card">
                                <div class="bd-card-header">
                                    <b>Help</b>
                                    <button
                                        type="button"
                                        class="bd-dialog-closer bd-button bd-button-icon bd-surface-10"
                                    >
                                        <bd-icon value="xmark"></bd-icon>
                                    </button>
                                </div>
                                <div class="bd-card-body">
                                    <p>${this.renderDescription()}</p>
                                </div>
                            </div>
                        </template>
                    </bd-dialog>
                </div>
                <small class="description">${this.renderDescription()}</small>
            </fieldset>
        `;
    }

    private renderDescription() {
        return html`
            This is a virtual plant grower that is based on LSystems.
            <br />
            <br />
            You can enter any text you want into the <b>seed</b> input, allowing
            you to customize your plant.
            <br />
            <br />
            With the <b>age</b> slider you can control the growth/age of the
            plant.
        `;
    }
}
